import React from 'react';
import i18n from '../../i18n';
import useSetState from '../../modules/hooks/useSetState';
import BaseInput from '../../component/form/base-input.component';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import { ReservePeriod } from '../../models/reserve.entity';
import moment from 'moment';
import _ from 'lodash';
import useDebounceEffect from '../../modules/hooks/useDebounceEffect';
import GlobalNotification from '../../component/common/snackbar';
import CommonInput from '../../component/form/common-input.component';  
import { ReactComponent as CloseIcon } from '../../static/images/svg/mingcute_close-fill.svg';

/** 主按钮：设计稿 Figma 1681:23374 — 主题紫、白字、8px 圆角、投影 */
const primaryActionSx = {
    textTransform: 'none' as const,
    borderRadius: '8px',
    px: 2,
    py: 1,
    fontWeight: 500,
    fontSize: '20px',
    lineHeight: '24px',
    boxShadow: '0 0 4px 0 var(--shadow-25)',
    bgcolor: 'var(--fill-primary-300)',
    color: 'var(--fill-neutral-100)',
    border: '1px solid var(--stroke-primary-100)',
    '&:hover': {
        bgcolor: 'var(--fill-primary-300)',
        filter: 'brightness(0.95)',
    },
    '&.Mui-disabled': {
        color: 'var(--fill-neutral-100)',
        opacity: 0.5,
    },
};

function ResrvePeroidCard({
    period,
    onDeletePeriod,
    onAddPeroidTimeTab,
    onChangePeriodName,
    onDeletePeroidTimeTab,
    asyncPeroidTime,
}: Props) {
    const emptyData = moment('2020-01-01 00:00:00').toDate();

    const [state, setState] = useSetState<State>({
        startTime: emptyData as any,
        endTime: emptyData as any,
        peroidName: period.name || '',
    });

    const { startTime, endTime, peroidName } = state;
    const pickerAnchorId = `reserve-period-picker-${period.code}`;

    const debounceText = useDebounceEffect(peroidName, { wait: 1000 });

    React.useEffect(() => {
        onChangePeriodName(debounceText);
    }, [debounceText]);

    const onDeletePeriodAction = () => {
        onDeletePeriod();
    };

    const onAddPeroidTimeTabAction = () => {
        const newTab = {
            start_time: moment(startTime).format('HH:mm'),
            end_time: moment(endTime).format('HH:mm'),
        };
        const endBeforeStartTimeFlag: boolean = newTab.start_time > newTab.end_time;
        if (endBeforeStartTimeFlag) {
            GlobalNotification.error(i18n.t('business_hour_page_starttime_less_then_endtime'));
            return;
        }
        onAddPeroidTimeTab(newTab);
    };

    const onDeletePeroidTimeTabAction = (dIndex: number, tid: number) => {
        const _curPeroid = _.cloneDeep(period);
        _curPeroid.date.splice(dIndex, 1);
        onDeletePeroidTimeTab(_curPeroid, tid);
    };

    const onChangePeriodNameAction = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setState({
            peroidName: value,
        });
    };

    const handleDateChange = (value: {
        startTime: Date | null;
        endTime: Date | null;
    }) => {
        const transform = (v: Date | null, key: 'startTime' | 'endTime') => {
            let newValue = moment(v).format(key === 'startTime' ? 'HH:mm' : 'kk:mm');
            if (moment(v).hour() === 0 && moment(v).minute() !== 0) {
                newValue = moment(v).format('HH:mm');
            }
            return moment(newValue, 'kk:mm').toDate();
        };
        setState({ startTime: transform(value.startTime, 'startTime'), endTime: transform(value.endTime, 'endTime') });
    };

    return (
        <Box id={`reserve-period-card-${period.code}`} className='reserve-period-card'>
            <Typography component='p' className='reserve-period-card__field-label'>
                {i18n.t('general_text_name')}
            </Typography>
            <Box className='reserve-period-card__name-row'>
                <Box className='reserve-period-card__name-input-wrap'>
                    <BaseInput
                        variant='outlined'
                        hiddenLabel
                        placeholder={i18n.t('staff_setting_page_enter_type_name')}
                        fullWidth
                        size='small'
                        value={peroidName}
                        onChange={onChangePeriodNameAction}
                        allowClear
                    />
                </Box>
                <Box className='reserve-period-card__actions'>
                    <Button
                        variant='contained'
                        disableElevation
                        sx={primaryActionSx}
                        disabled={period?.date.length < 1}
                        onClick={asyncPeroidTime}
                    >
                        {i18n.t('reserve_meal_page_time_async_to_other_day')}
                    </Button>
                    <Button
                        variant='contained'
                        disableElevation
                        sx={primaryActionSx}
                        onClick={onDeletePeriodAction}
                    >
                        {i18n.t('reserve_meal_page_delete_order_time')}
                    </Button>
                </Box>
            </Box>

            <Typography component='p' className='reserve-period-card__section-title'>
                {i18n.t('reserve_meal_page_order_time_setting')}
            </Typography>

            <Box className='reserve-period-card__chips-row'>
                {(period?.date || []).map((dItem, dIndex) => (
                    <Box key={`${dItem.code}-${dIndex}`} className='reserve-period-card__time-chip'>
                        <Typography component='span' className='reserve-period-card__time-chip-text'>
                            {`${dItem.start_time} - ${dItem.end_time}`}
                        </Typography>
                        <IconButton
                            size='small'
                            className='reserve-period-card__time-chip-close'
                            onClick={() => onDeletePeroidTimeTabAction(dIndex, dItem.time_id)}
                            aria-label={i18n.t('general_text_delete')}
                        >
                            <CloseIcon aria-hidden />
                        </IconButton>
                    </Box>
                ))}
            </Box>

            <Box id={pickerAnchorId} className='reserve-period-card__picker-row'>
                <Box className='reserve-period-card__time-range-wrap'>
                    <CommonInput
                        inputType='TIME_RANGE_COMPACT'
                        rangeHandleChangePicker={(dates) => {
                            if (dates) {
                                handleDateChange({ startTime: dates[0], endTime: dates[1] });
                            }
                        }}
                        rangeValue={[startTime, endTime]}
                        size='small'
                    />
                </Box>
                <IconButton
                    className='reserve-period-card__picker-add'
                    onClick={onAddPeroidTimeTabAction}
                    aria-label={i18n.t('general_text_add')}
                >
                    <Add sx={{ fontSize: 28 }} />
                </IconButton>
            </Box>
        </Box>
    );
}

export default ResrvePeroidCard;

interface Props {
    period: ReservePeriod;
    onDeletePeriod: () => void;
    onAddPeroidTimeTab: (newTab: { start_time: string; end_time: string }) => void;
    onChangePeriodName: (value: string) => void;
    onDeletePeroidTimeTab: (period: ReservePeriod, tid: number) => void;
    asyncPeroidTime: () => void;
}

type State = {
    startTime: Date;
    endTime: Date;
    peroidName: string;
};
